// import { useState, useMemo } from 'react';
// import { Link } from 'react-router-dom';
// import { MessageCircle, User as UserIcon, Star } from 'lucide-react';

// interface UserAvatarProps {
//   id: string | number;
//   name?: string;
//   image?: string;
//   width?: number;
//   height?: number;
//   hideName?: boolean;
//   isFavorite?: boolean;
//   starTooltip?: string;
// }

// export default function UserAvatar({
//   id,
//   name,
//   image,
//   width = 56,
//   height = 56,
//   hideName = false,
//   isFavorite = false,
//   starTooltip = 'Favorite'
// }: UserAvatarProps) {
//   const [imgError, setImgError] = useState(false);

//   // const initials = useMemo(() => {
//   //   if (!name) return '';
//   //   const parts = name.trim().split(/\s+/);
//   //   return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
//   // }, [name]);

//   // Deterministic pastel bg color from name
//   const bgColor = useMemo(() => {
//     if (!name) return 'bg-white';
//     let hash = 0;
//     for (let i = 0; i < name.length; i++) {
//       hash = name.charCodeAt(i) + ((hash << 5) - hash);
//     }
//     const hue = Math.abs(hash) % 360;
//     return `bg-[hsl(${hue}deg_65%_45%)]`;
//   }, [name]);

//   const showFallback = !image || imgError;

//   return (
//     <div className="flex items-center gap-3">
//       {/* Avatar -> Chat */}
//       <Link
//         to="/dashboards/messenger"
//         state={{ id }}
//         aria-label="Open chat"
//         className="relative inline-block group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full"
//       >
//         <div
//           className={`rounded-full text-black flex items-center justify-center overflow-hidden ring-2 ring-gray-300 shadow-md transition-all group-hover:ring-blue-500 ${
//             showFallback ? bgColor : 'bg-blue-600'
//           }`}
//           style={{ width, height, fontSize: 12 }}
//         >
//           {showFallback ? (
//             // initials ? (
//             //   <span className="font-semibold text-sm select-none">
//             //     {initials.toUpperCase()}
//             //   </span>
//             // ) : (
//             <UserIcon className="w-1/2 h-1/2 opacity-90" />
//           ) : (
//             // )
//             <img
//               src={image}
//               alt={name || 'user avatar'}
//               className="w-full h-full object-cover"
//               loading="lazy"
//               draggable={false}
//               onError={() => setImgError(true)}
//             />
//           )}
//         </div>

//         {isFavorite && (
//           <div
//             title={starTooltip}
//             className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center z-20"
//           >
//             <Star className="w-4 h-4 text-yellow-500 fill-yellow-400" />
//           </div>
//         )}

//         {/* Chat tooltip (below) */}
//         <span
//           className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 mt-2
//                      rounded-md bg-black/80 text-white text-xs px-2 py-1 opacity-0
//                      group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-30"
//         >
//           Chat
//           <span className="absolute left-1/2 -top-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-black/80" />
//         </span>
//       </Link>

//       {/* Name -> Profile */}
//       {!hideName && name && (
//         <Link
//           to="/dashboards/contractorsDetails"
//           state={{ user: { id, name, image } }}
//           className="relative group text-sm font-semibold text-gray-800 leading-none hover:text-blue-600 transition-colors"
//         >
//           {name}
//           <span
//             className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 mt-2
//                        rounded-md bg-black/80 text-white text-xs px-2 py-1 opacity-0
//                        group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-30"
//           >
//             Profile
//             <span className="absolute left-1/2 -top-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-black/80" />
//           </span>
//         </Link>
//       )}
//     </div>
//   );
// }

import { useState, useMemo, ElementType } from 'react';
import { Link } from 'react-router-dom';
import { User as UserIcon, Star } from 'lucide-react';

interface UserAvatarProps {
  id: string | number;
  name?: string;
  image?: string;
  width?: number;
  height?: number;
  hideName?: boolean;
  isFavorite?: boolean;
  starTooltip?: string;
  profileTo?: string | null;
  profileState?: Record<string, unknown>;
  readOnly?: boolean;
  /** Opens the in-context event thread (#4005) instead of the Messenger page. */
  onOpenChat?: () => void;
  chatEventId?: number;
}

export default function UserAvatar({
  id,
  name,
  image,
  width = 56,
  height = 56,
  hideName = false,
  isFavorite = false,
  starTooltip = 'Favorite',
  profileTo = '/dashboards/contractorsDetails',
  profileState,
  readOnly = false,
  onOpenChat,
  chatEventId
}: UserAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const size = Math.min(width, height);

  // Dynamic sizing
  const starWrapperSize = Math.round(Math.min(Math.max(size * 0.34, 16), 30)); // 16–30px
  const starIconSize = Math.round(starWrapperSize * 0.68); // ~68% of wrapper
  const nameFontSize =
    size >= 80 ? 18 : size >= 64 ? 16 : size >= 52 ? 14 : size >= 44 ? 13 : 12; // px
  const gap = size >= 72 ? 14 : size >= 56 ? 10 : 8;

  const bgColor = useMemo(() => {
    if (!name) return 'bg-white';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `bg-[hsl(${hue}deg_65%_45%)]`;
  }, [name]);

  const showFallback = !image || imgError;
  const AvatarContainer: ElementType = readOnly ? 'span' : onOpenChat ? 'button' : Link;
  const chatProps: Record<string, unknown> = readOnly
    ? {}
    : onOpenChat
      ? { type: 'button', onClick: onOpenChat, 'aria-label': 'Open chat' }
      : { to: '/dashboards/messenger', state: { id, ...(chatEventId ? { eventId: chatEventId } : {}) }, 'aria-label': 'Open chat' };

  return (
    <div className="flex items-center" style={{ gap }}>
      {/* Avatar -> Chat */}
      <AvatarContainer
        {...chatProps}
        className="relative inline-block group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full"
      >
        <div
          // className={`rounded-full flex items-center justify-center overflow-hidden ring-2 ring-gray-300 shadow-md transition-all group-hover:ring-blue-500 ${
          //   showFallback ? `${bgColor} text-black` : 'bg-blue-600'
          // }`}
          className={`rounded-full flex items-center justify-center overflow-hidden ring-2 shadow-md transition-all
    ${
      showFallback
        ? 'bg-white text-gray-600 ring-gray-300'
        : 'bg-blue-600 ring-gray-300 group-hover:ring-blue-500'
    }
  `}
          style={{
            width,
            height,
            fontSize: size * 0.22 // for potential initials later
            // ringColor: 'white'
          }}
        >
          {showFallback ? (
            <UserIcon
              className="opacity-90"
              style={{ width: size * 0.5, height: size * 0.5 }}
            />
          ) : (
            <img
              src={image}
              alt={name || 'user avatar'}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
              onError={() => setImgError(true)}
            />
          )}
        </div>

        {isFavorite && (
          <div
            title={starTooltip}
            className="absolute flex items-center justify-center rounded-full bg-white shadow z-1"
            style={{
              width: starWrapperSize,
              height: starWrapperSize,
              top: -starWrapperSize * 0.28,
              right: -starWrapperSize * 0.28
            }}
          >
            <Star
              className="text-yellow-500 fill-yellow-400"
              style={{ width: starIconSize, height: starIconSize }}
            />
          </div>
        )}

        {/* Chat tooltip (below) */}
        {!readOnly && <span
          className="pointer-events-none absolute left-1/2 bottom-full -translate-x-1/2 mb-2
                     rounded-md bg-black/80 text-white px-2 py-1 opacity-0 text-[11px]
                     group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-30"
        >
          Chat
          <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-black/80" />
        </span>}
      </AvatarContainer>

      {/* Name -> Profile */}
      {!hideName && name && profileTo && !readOnly && (
        <Link
          to={profileTo}
          state={profileState ?? { user: { id, name, image } }}
          aria-label={`Open ${name} profile`}
          className="relative group font-semibold text-gray-800 leading-none hover:text-blue-600 transition-colors bg-white px-2 py-1.5 rounded-xl whitespace-nowrap"
          style={{ fontSize: nameFontSize }}
        >
          {name}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 bottom-full -translate-x-1/2 mb-2
                       rounded-md bg-black/80 text-white text-[11px] px-2 py-1 opacity-0
                       group-hover:opacity-100 group-focus:opacity-100 transition-opacity z-30"
          >
            Profile
            <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-black/80" />
          </span>
        </Link>
      )}
      {!hideName && name && (!profileTo || readOnly) && (
        <span
          className="font-semibold text-gray-800 leading-none bg-white px-2 py-1.5 rounded-xl whitespace-nowrap"
          style={{ fontSize: nameFontSize }}
        >
          {name}
        </span>
      )}
    </div>
  );
}
